import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '@core/services/auth/auth.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { USER_ONBOARDING_STEP_ROUTES } from '@app/onboarding-shell/services/user-onboarding.service';
import { UIFeedbackService } from '../../services/ui-feedback.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Observable, combineLatest } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';
import { WhatsappConnectDialogComponent } from './whatsapp-connect-dialog.component';

/**
 * Raison structurée renvoyée par `resendWhatsappConfirmation` dans
 * `error.details.reason` (#661), ou `null` si le serveur n'en donne pas.
 */
export function whatsappResendFailureReason(error: unknown): string | null {
  const details = (error as { details?: { reason?: unknown } } | null)?.details;
  return typeof details?.reason === 'string' ? details.reason : null;
}

@Component({
    selector: 'app-email-verification-banner',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatIconModule,
        MatButtonModule,
        TranslatePipe
    ],
    template: `
    @if (shouldShowBanner$ | async) {
      <div class="verification-banner" role="status">
        <div class="banner-inner">
          <span class="leading">
            <span class="dot" aria-hidden="true">
              <mat-icon>mark_email_unread</mat-icon>
            </span>
            <span class="text">
              <strong>{{ 'shared.emailVerification.title' | translate }}</strong>
              <!--
                Deux chemins mènent au même déverrouillage depuis #427, et le
                bandeau doit nommer celui qui est déjà en route — DESIGN.md §13,
                un blocage dit ce qui le lève. Renvoyer au seul e-mail quand un
                message WhatsApp attend d'être ouvert, c'est envoyer la personne
                chercher là où elle a déjà cherché.
              -->
              <span class="subtle">{{
                ((whatsappPending$ | async)
                  ? 'shared.emailVerification.blockedSubtitleWhatsapp'
                  : 'shared.emailVerification.blockedSubtitle') | translate
              }}</span>
            </span>
          </span>

          <span class="actions">
            @if (whatsappPending$ | async) {
              <button
                type="button"
                mat-button
                class="secondary-action"
                (click)="resendWhatsapp()"
                [disabled]="resendingWhatsapp || whatsappResendSpent"
              >
                {{ (resendingWhatsapp ? 'shared.emailVerification.sending' : 'shared.emailVerification.resendWhatsapp') | translate }}
              </button>
            } @else if (canConnectWhatsapp$ | async) {
              <button
                type="button"
                mat-button
                class="secondary-action"
                (click)="openWhatsappConnect()"
              >
                {{ 'shared.emailVerification.addWhatsapp' | translate }}
              </button>
            }
            <button
              type="button"
              mat-button
              class="primary-action"
              (click)="resendVerification()"
              [disabled]="resending"
            >
              {{ (resending ? 'shared.emailVerification.sending' : 'shared.emailVerification.resend') | translate }}
            </button>
            <button
              type="button"
              mat-button
              class="secondary-action"
              (click)="refreshStatus()"
              [disabled]="refreshing"
            >
              {{ (refreshing ? 'shared.emailVerification.checking' : 'shared.emailVerification.verified') | translate }}
            </button>
          </span>
        </div>
      </div>
    }
    `,
    styles: [`
    :host {
      display: block;
      width: 100%;
    }

    .verification-banner {
      width: 100%;
      box-sizing: border-box;
      /* Warning-styled. Positioning (fixed, top-pinned, z-index) is owned by the
         global banner stack wrapper in app.component — the founder's requirement
         that an unverified user can never miss it is satisfied there, and the
         stack offsets the toolbar so this no longer covers it. */
      background: rgb(var(--ds-warning-soft));
      border-bottom: 2px solid rgb(var(--ds-warning));
      box-shadow: 0 2px 6px rgba(180, 83, 9, 0.18);
    }

    .banner-inner {
      max-width: 1280px;
      margin: 0 auto;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .leading {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
    }

    .dot {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgb(var(--ds-warning));
      color: rgb(var(--ds-surface));
      flex-shrink: 0;

      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    .text {
      display: flex;
      align-items: baseline;
      gap: 8px;
      flex-wrap: wrap;
      min-width: 0;

      strong {
        font-weight: 700;
        font-size: 13px;
        color: rgb(var(--ds-warning));
      }

      .subtle {
        font-size: 12px;
        color: rgb(var(--ds-warning));
        line-height: 1.4;
      }
    }

    .actions {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    .primary-action,
    .secondary-action {
      font-size: 12px !important;
      padding: 4px 10px !important;
      min-height: 30px !important;
      line-height: 1 !important;
    }

    .primary-action {
      background: rgb(var(--ds-warning)) !important;
      color: rgb(var(--ds-surface)) !important;
      font-weight: 600 !important;
      border-radius: 6px !important;
    }

    .secondary-action {
      color: rgb(var(--ds-warning)) !important;
      font-weight: 600 !important;
    }

    @media (max-width: 768px) {
      .banner-inner {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        padding: 10px 14px;
      }

      .text {
        flex-direction: column;
        gap: 2px;
      }

      .actions {
        justify-content: flex-end;
      }
    }
  `]
})
export class EmailVerificationBannerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private readonly uiFeedback = inject(UIFeedbackService);
  private readonly dialog = inject(MatDialog);

  shouldShowBanner$: Observable<boolean>;
  resending = false;
  refreshing = false;

  private readonly fns = inject(Functions);

  /**
   * Un message WhatsApp attend-il d'être ouvert ?
   *
   * Vrai quand la personne a coché « joignable sur WhatsApp » et n'a pas encore
   * ouvert le lien reçu. C'est exactement la population pour qui le second
   * chemin existe : ni ceux qui n'ont pas donné de numéro, ni ceux qui l'ont
   * déjà confirmé — ces derniers ne voient plus ce bandeau du tout depuis #427.
   */
  readonly whatsappPending$: Observable<boolean>;

  /**
   * Afficher « Ajouter mon WhatsApp » quand aucun consentement n'est en cours
   * (et que le numéro n'est pas déjà confirmé). Mutuellement exclusif avec
   * `whatsappPending$` — le template utilise `@else if`.
   */
  readonly canConnectWhatsapp$: Observable<boolean>;

  /**
   * Le crédit de rattrapage est-il consommé ?
   *
   * Purement cosmétique : c'est le backend qui plafonne, sur la CONVERSATION,
   * donc un rechargement ne rend pas un second envoi. Ce drapeau évite juste de
   * proposer un bouton dont on sait déjà qu'il répondra « déjà envoyé ».
   */
  resendingWhatsapp = false;
  whatsappResendSpent = false;

  constructor(
    private authService: AuthService,
    private authStore: AuthStore,
    private auth: Auth,
    private translate: TranslateService
  ) {
    // Suppress only on auth routes (login/register) where the banner would be
    // redundant noise. We DO show it during onboarding: a freshly signed-up
    // user lands straight in the onboarding flow, so that's exactly where the
    // "check your inbox and verify" guidance needs to be visible.
    const onSuppressedRoute$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => this.isSuppressedUrl(e.urlAfterRedirects)),
      startWith(this.isSuppressedUrl(this.router.url)),
    );

    // Show for ANY authenticated, unverified account — not just first-time
    // signups. Sending mail through Proporio is blocked until the address is
    // verified (see EmailVerificationGuardService + the backend mailDispatcher
    // gate), so every unverified user needs the prompt + path. The banner is
    // intentionally NON-dismissible: it must stay visible the whole time the
    // address is unverified (the founder's explicit requirement). Gated on
    // `userLoaded` so it never flashes before the real verification status has
    // been read (the status seed is `false`).
    // Déclaré AVANT `shouldShowBanner$`, qui le consomme.
    this.whatsappPending$ = toObservable(this.authStore.user).pipe(
      map((user) => {
        const profile = user?.profile;
        return !!profile?.whatsappOptIn && !profile?.whatsappVerifiedAt;
      }),
      takeUntil(this.destroy$)
    );

    this.canConnectWhatsapp$ = toObservable(this.authStore.user).pipe(
      map((user) => {
        const profile = user?.profile;
        // Pas d'opt-in (éventuellement un numéro déjà saisi ailleurs) et pas
        // encore confirmé via WhatsApp → proposer le dialogue. Quand l'opt-in
        // est vrai sans verifiedAt, c'est `whatsappPending$` qui gagne.
        return !profile?.whatsappOptIn && !profile?.whatsappVerifiedAt;
      }),
      takeUntil(this.destroy$)
    );

    this.shouldShowBanner$ = combineLatest([
      toObservable(this.authStore.isAuthenticated),
      toObservable(this.authStore.userLoaded),
      this.authService.getEmailVerificationStatus(),
      onSuppressedRoute$,
    ]).pipe(
      map(([isAuthenticated, userLoaded, isEmailVerified, suppressed]) => {
        /*
         * `whatsappPending` ne ferme PLUS le bandeau — il en change le contenu.
         *
         * L'intention de #427 était juste et reste tenue : quand une
         * confirmation WhatsApp est en vol, on n'envoie personne fouiller sa
         * boîte mail. Mais la fermer entièrement a eu un effet que personne
         * n'avait vu : le bouton « renvoyer par WhatsApp » et la copie
         * `blockedSubtitleWhatsapp` vivent DANS ce bandeau, sous un
         * `@if (whatsappPending$)`. Une condition exigeait `whatsappPending`,
         * l'autre `!whatsappPending` : les deux s'excluaient, donc le bouton
         * n'a jamais pu être rendu une seule fois.
         *
         * Conséquence pour la personne dont le message WhatsApp s'est perdu :
         * ni bandeau, ni bouton, ni explication — un compte qui ne s'ouvre pas
         * et rien pour le débloquer. C'est exactement ce que DESIGN.md §13
         * interdit : un blocage doit dire ce qui le lève.
         *
         * Le bandeau s'affiche donc dans les deux cas, et c'est la COPIE qui
         * porte la nuance : `blockedSubtitleWhatsapp` nomme le message en vol
         * au lieu de réclamer l'e-mail, et le bouton de renvoi WhatsApp
         * apparaît à côté. Recette du 2026-09-07, point P5.
         */
        return isAuthenticated &&
               userLoaded &&
               !isEmailVerified &&
               !suppressed;
      }),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Les écrans où ce bandeau se tait.
   *
   * Les routes d'auth, parce qu'il y serait redondant. Et depuis #427 la phase
   * « À propos de vous » de l'onboarding : c'est là que le numéro se saisit
   * (étape 1/5), et un numéro confirmé ouvre désormais le compte aussi bien
   * qu'un lien e-mail. Réclamer l'adresse avant que la personne ait seulement
   * fini de se décrire, alors que le message WhatsApp part pendant cette phase,
   * c'est promettre un travail inutile.
   *
   * Le bandeau réapparaît dès `/onboarding/welcome` — la suite du parcours n'a
   * plus rien à voir avec la preuve de possession.
   *
   * Les segments viennent de `USER_ONBOARDING_STEP_ROUTES`, jamais écrits en
   * dur : une étape renommée ne doit pas ressusciter le bandeau en silence.
   * Même motif que `core/guards/onboarding.guard.ts`.
   */
  private isSuppressedUrl(url: string): boolean {
    if (!url) return false;
    return (
      url.startsWith('/auth') ||
      url.startsWith('/login') ||
      url.startsWith('/register') ||
      USER_ONBOARDING_STEP_ROUTES.some(
        (step) => url.startsWith(`/onboarding/${step}`)
      )
    );
  }

  ngOnInit(): void {
    // No special initialization needed - banner is in normal document flow
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async resendVerification(): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      this.uiFeedback.toast.info(this.translate.instant('shared.emailVerification.noUser'));
      return;
    }

    this.resending = true;
    this.cdr.markForCheck();
    try {
      // Use the custom resend function from auth service, rendered in the
      // language the user is reading (it used to always mail in French).
      await this.authStore.resendCustomEmailVerification(
        undefined,
        this.translate.currentLang() ?? undefined
      );
      this.uiFeedback.toast.success(this.translate.instant('shared.emailVerification.sent'));
    } catch (error) {
      console.error('Error sending verification email:', error);
      this.uiFeedback.toast.error(this.translate.instant('shared.emailVerification.sendFailed'));
    } finally {
      this.resending = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Ouvre le dialogue téléphone + consent WhatsApp.
   * Au succès l'écriture Firestore déclenche l'activation ; le snapshot user
   * bascule le bandeau vers « Renvoyer WhatsApp ».
   */
  openWhatsappConnect(): void {
    const user = this.authStore.user();
    const profile = user?.profile;
    const initialPhone =
      (typeof profile?.phone === 'string' && profile.phone.trim()) ||
      user?.phoneNumber?.trim() ||
      user?.mobileNumber?.trim() ||
      '';

    const ref = this.dialog.open(WhatsappConnectDialogComponent, {
      panelClass: ['dialog-size-medium'],
      autoFocus: 'input',
      data: { initialPhone },
    });

    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((ok) => {
      if (!ok) return;
      this.uiFeedback.toast.success(
        this.translate.instant('shared.emailVerification.whatsappConnect.sent'),
      );
      this.cdr.markForCheck();
    });
  }

  /**
   * Renvoyer, une fois, le message WhatsApp qui porte le lien d'activation.
   *
   * Le numéro n'est PAS transmis : le backend le lit sur la fiche du demandeur.
   * L'accepter du client ferait de ce bouton un moyen d'écrire au numéro de
   * quelqu'un d'autre depuis notre numéro d'entreprise.
   *
   * Le message d'erreur du backend est affiché tel quel quand il y en a un :
   * il nomme la réparation (« corrigez le numéro », « envoyez START »), ce
   * qu'un libellé générique côté client ne saurait pas faire.
   */
  async resendWhatsapp(): Promise<void> {
    this.resendingWhatsapp = true;
    this.cdr.markForCheck();
    try {
      const resend = httpsCallable<
        Record<string, never>,
        { sent: boolean; reason: string | null; remaining: number }
      >(this.fns, 'resendWhatsappConfirmation');
      const { data } = await resend({});
      if (data.remaining <= 0) this.whatsappResendSpent = true;
      this.uiFeedback.toast.success(
        this.translate.instant('shared.emailVerification.whatsappSent')
      );
    } catch (error) {
      const code = (error as { code?: string })?.code ?? '';
      if (code.endsWith('resource-exhausted')) this.whatsappResendSpent = true;
      const reason = whatsappResendFailureReason(error);
      // Numéro déjà confirmé sur un autre compte (#661) : renvoyer ne servira
      // jamais, on désactive le bouton et on renvoie vers l'e-mail.
      if (reason === 'verified-elsewhere') this.whatsappResendSpent = true;
      const detail = (error as { message?: string })?.message;
      this.uiFeedback.toast.error(
        reason === 'verified-elsewhere'
          ? this.translate.instant('shared.emailVerification.whatsappVerifiedElsewhere')
          : detail || this.translate.instant('shared.emailVerification.whatsappFailed')
      );
    } finally {
      this.resendingWhatsapp = false;
      this.cdr.markForCheck();
    }
  }

  async refreshStatus(): Promise<void> {
    this.refreshing = true;
    this.cdr.markForCheck();
    try {
      const isVerified = await this.authStore.refreshEmailVerificationStatus();
      if (isVerified) {
        this.uiFeedback.toast.info(this.translate.instant('shared.emailVerification.confirmed'));
      } else {
        this.uiFeedback.toast.info(this.translate.instant('shared.emailVerification.notYet'));
      }
    } catch (error) {
      console.error('Error checking email verification status:', error);
      this.uiFeedback.toast.error(this.translate.instant('shared.emailVerification.checkFailed'));
    } finally {
      this.refreshing = false;
      this.cdr.markForCheck();
    }
  }
}
